/**
 * The start-screen your-turn notification's data + DOM knowledge (multiplayer-entry-and-
 * notifications design § "Your-turn banners"). Since start-screen-layout-improvements part B,
 * this module stopped touching the screen — it produces data (`readStampTurnRuns`/
 * `fetchTurnRuns`) and detached DOM (`buildTurnBannerBlock`) only; `game-start-multiplayer-
 * section.js` owns all mounting, since the block now lives inside the Multiplayer section's
 * `.mp-panel-notice`, not above the logo (the section-restructure freed it from the height
 * solve entirely — the panel is `measureMenuH`-excluded, so an awaiting player's phone no
 * longer renders a smaller menu).
 *
 * Post-acceptance restructure (2026-08-10, Andre on rendered pixels — see the PR body for the
 * full record): a single headline above the row stack, then one terse row per awaited run — no
 * per-row sentence, no aggregate banner. The headline always states the true count (`Du bist
 * dran`/`Your turn` for exactly one, `{n} Tische warten auf dich`/`{n} tables waiting for you`
 * for more) even when the row stack itself is capped at 3 — reaching the untabled remainder is
 * the Multiplayer section itself, already showing the block. A row carries only the mode icon
 * (amber, unchanged) and the other members' names — `&`-joined, never a comma (three seats
 * means at most two other humans, and a comma between exactly two names reads wrong in
 * German). Stripping the sentence out of the row would otherwise flatten its accessible name
 * to just the names, so each row's `aria-label` composes the full sentence server-side style —
 * `${mode} ${multiplayerWith} ${names}, ${multiplayerYourTurn}` — mirroring
 * `templates/multiplayer.html`'s `cardLabel` composition on the run cards this same run-card
 * language descends from.
 *
 * `readStampTurnRuns()` reads the PRISTINE render's shell-stamped runs
 * (`GameController#index`'s `data-your-turn-runs`) synchronously — no fetch, no layout shift.
 * `fetchTurnRuns()` is every later, NON-pristine render's (Quit-return, error-recovery) lean
 * refetch — deliberately not `GET /api/multiplayer/runs`, which replays every run's tally via
 * `ReconstructedRunAssembler` and isn't safe to fire on an error-recovery render that wasn't
 * user-initiated (design § "Your-turn banners"). Which of the two the caller uses is
 * `game-start-multiplayer-section.js`'s own `pristine`-threaded choice, mirroring the same
 * `pristine` param `buildMultiplayerSection` now takes from `buildGameStartContent`.
 */
import { t } from "../i18n.js";
import {
  createChalice,
  createFallenChalice,
} from "../icons/game-type-icons.js";
import { enterRun } from "./game-start-session.js";

const CAP = 3;
// why: matches --ui-warning (skat.css) — the mode icon's amber, distinct from the run cards'
// neutral EMBLEM_COLOR (multiplayer-tables.js): a banner is a notification, not a browse surface.
const ICON_COLOR = "#D97706";
const ICON_SIZE = 24; // play-test: fits the banner row without dominating the text

/** Sync read of the pristine render's shell-stamped turn runs — the `data-your-turn-runs`
 *  attribute `GameController#index` writes on `#skat-app`. Empty array both when the node or
 *  the attribute is absent (a non-pristine render's stamp is stale by the time the section
 *  mounts, which is exactly why non-pristine renders call `fetchTurnRuns` instead). */
export function readStampTurnRuns() {
  const raw = document.getElementById("skat-app")?.dataset.yourTurnRuns;
  return raw == null ? [] : JSON.parse(raw);
}

/** Async lean-endpoint fetch for every non-pristine render. Resolves `[]` on any failure
 *  (network error or a non-OK response) — the caller treats "nothing to show" and "the fetch
 *  failed" identically. */
export function fetchTurnRuns() {
  return fetch("/api/multiplayer/your-turn")
    .then((response) => (response.ok ? response.json() : []))
    .catch((e) => {
      console.warn(
        "[game-start-turn-banners] refresh failed:",
        e?.message ?? e,
      );
      return [];
    });
}

/** How this project says "n tables await you", in one place. The n==1/n>1 split (not one
 *  templated string) sidesteps the awkward German singular "1 Tisch wartet auf dich" and gives
 *  the common single-table case its own crisp line — reusing multiplayerYourTurn rather than
 *  minting a near-duplicate key (its "Du bist dran"/"Your turn" is exactly the n==1 text, and
 *  the run-card aria-label sentence below draws on it too).
 *
 *  Exported for the section's bar badge (`game-start-multiplayer-section.js#applyBadge`), which
 *  needs the same sentence as this module's own headline: the badge renders a bare digit, so the
 *  collapsed bar's accessible name would otherwise read "Multiplayer 1". Same count, same
 *  wording — one knowledge unit, not two call sites re-deriving the split. */
export function turnCountSentence(count) {
  return count === 1
    ? t("multiplayerYourTurn")
    : t("multiplayerBannerHeadlineMulti", count);
}

function buildHeadline(count) {
  const headline = document.createElement("p");
  headline.className = "game-start-turn-banners-headline";
  headline.textContent = turnCountSentence(count);
  return headline;
}

function buildOneBanner(turn) {
  const banner = document.createElement("button");
  banner.type = "button";
  banner.className = "game-start-turn-banner";
  banner.dataset.gameId = turn.gameId; // mirrors .mp-run's own convention (multiplayer-tables.js)
  banner.appendChild(buildIcon(turn.gameMode));
  const text = document.createElement("span");
  text.className = "game-start-turn-banner-text";
  // "&"-joined, never a comma (file header) — visible text is the bare identifier now, the
  // headline above carries the "your turn" framing.
  const names = turn.othersNames.join(" & ");
  text.textContent = names;
  banner.appendChild(text);
  // The row's accessible name, composed the same way templates/multiplayer.html's `cardLabel`
  // composes the run card's — mode word + multiplayerWith + names + multiplayerYourTurn — since
  // stripping the sentence out of the visible text (above) would otherwise flatten the button's
  // accessible name to just the names, losing both the mode and the turn fact.
  const modeWord = turn.gameMode === "RAMSCH" ? t("modeRamsch") : t("modeSkat");
  banner.setAttribute(
    "aria-label",
    `${modeWord} ${t("multiplayerWith")} ${names}, ${t("multiplayerYourTurn")}`,
  );
  banner.addEventListener("click", () => enterRun(turn.gameId, "banner"));
  return banner;
}

function buildIcon(gameMode) {
  const wrap = document.createElement("span");
  wrap.className = "game-start-turn-banner-icon";
  wrap.setAttribute("aria-hidden", "true");
  wrap.appendChild(
    gameMode === "RAMSCH"
      ? createFallenChalice(ICON_SIZE, ICON_COLOR)
      : createChalice(ICON_SIZE, ICON_COLOR),
  );
  return wrap;
}

/** Pure DOM: the headline + capped row stack — `null` when `turns` is empty (the caller's
 *  null-return-skip idiom). The section owns
 *  mounting (into `.mp-panel-notice`) and the non-pristine reveal-fade class; this function
 *  builds detached DOM only. */
export function buildTurnBannerBlock(turns) {
  if (turns.length === 0) return null;
  const container = document.createElement("div");
  container.className = "game-start-turn-banners";
  // Newest-first order arrives pre-sorted from the server (`MultiplayerRunSummaries#yourTurnRuns`
  // — `TurnRun` carries no timestamp for the client to re-sort by), so a plain slice for the row
  // cap is enough. The headline reads `turns.length` — the true total, not the capped row count —
  // since it's the one place the overflow is still stated (design § "Your-turn banners", the
  // 2026-08-10 restructure).
  container.appendChild(buildHeadline(turns.length));
  for (const turn of turns.slice(0, CAP)) {
    container.appendChild(buildOneBanner(turn));
  }
  return container;
}
